import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import Badge from "@/components/base/Badge";
import ConfirmDeleteModal from "@/components/base/ConfirmDeleteModal";
import EmptyState from "@/components/base/EmptyState";
import { cn } from "@/utils/cn";
import { formatDate } from "@/utils/format";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import {
  PLATFORM_SCOPE_LABELS,
  platformLocataireInitiales,
  platformLocataireNomComplet,
} from "@/store/platform/types";
import { TYPE_PIECE_LABELS, type TypePieceIdentite } from "@/pages/proprietaire/locataires/types";
import PlatformLocataireForm from "@/pages/super-admin/components/PlatformLocataireForm";

export default function SuperAdminLocataireDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { getLocataire, removeLocataire, countContratsByLocataire } = usePlatformData();

  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const locataire = id ? getLocataire(id) : undefined;

  if (!locataire) {
    return (
      <div className="flex flex-col gap-6">
        <Link
          to="/super-admin/locataires"
          className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" aria-hidden="true" />
          Retour aux locataires
        </Link>
        <EmptyState
          icon="ri-user-3-line"
          title="Locataire introuvable"
          description="Ce locataire n'existe pas ou n'est plus disponible."
          action={
            <Link
              to="/super-admin/locataires"
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
            >
              <i className="ri-arrow-left-line text-base" aria-hidden="true" />
              Voir tous les locataires
            </Link>
          }
        />
      </div>
    );
  }

  const nom = platformLocataireNomComplet(locataire);
  const piece = locataire.typePiece ? TYPE_PIECE_LABELS[locataire.typePiece as TypePieceIdentite] : "";
  const nbContrats = countContratsByLocataire(locataire.id);

  const infos: { label: string; value: string; icon: string }[] = [
    { label: "Espace", value: PLATFORM_SCOPE_LABELS[locataire.scope], icon: "ri-layout-grid-line" },
    { label: "Téléphone", value: locataire.telephone || "—", icon: "ri-phone-line" },
    { label: "Email", value: locataire.email || "—", icon: "ri-mail-line" },
    { label: "Profession", value: locataire.profession || "—", icon: "ri-briefcase-line" },
    { label: "Adresse", value: locataire.adresse || "—", icon: "ri-map-pin-2-line" },
    { label: "Pièce d'identité", value: piece ? `${piece}${locataire.numeroPiece ? ` · ${locataire.numeroPiece}` : ""}` : "—", icon: "ri-id-card-line" },
    { label: "Contact d'urgence", value: locataire.contactUrgence || "—", icon: "ri-contacts-line" },
    { label: "Créé le", value: formatDate(locataire.createdAt), icon: "ri-calendar-line" },
  ];

  const confirmDelete = () => {
    removeLocataire(locataire.id);
    setDeleteOpen(false);
    navigate("/super-admin/locataires");
  };

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/super-admin/locataires"
        className="inline-flex w-fit items-center gap-2 whitespace-nowrap text-sm font-label font-medium text-foreground-600 transition-colors hover:text-foreground-950 cursor-pointer"
      >
        <i className="ri-arrow-left-line text-base" aria-hidden="true" />
        Retour aux locataires
      </Link>

      <section className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-50 p-4 md:p-6 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex min-w-0 items-start gap-4">
          <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-primary-100 font-heading text-lg font-semibold text-primary-800">
            {platformLocataireInitiales(locataire)}
          </span>
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <Badge tone={locataire.scope === "agence" ? "accent" : "primary"}>
                {PLATFORM_SCOPE_LABELS[locataire.scope]}
              </Badge>
              <Badge tone="neutral" icon="ri-file-text-line">
                {nbContrats} contrat{nbContrats > 1 ? "s" : ""}
              </Badge>
            </div>
            <h2 className="mt-3 font-heading text-xl font-semibold text-foreground-950 md:text-2xl">{nom}</h2>
            <p className="mt-1 text-sm text-foreground-600">{locataire.profession || "Profession non renseignée"}</p>
          </div>
        </div>

        <div className="flex flex-col gap-2 sm:flex-row lg:shrink-0">
          <button
            type="button"
            onClick={() => setEditOpen(true)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-4 py-2.5 text-sm font-label font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            <i className="ri-edit-line text-base" aria-hidden="true" />
            Modifier
          </button>
          <button
            type="button"
            onClick={() => setDeleteOpen(true)}
            className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 bg-background-50 px-4 py-2.5 text-sm font-label font-medium text-primary-700 transition-colors hover:bg-primary-50 cursor-pointer"
          >
            <i className="ri-delete-bin-line text-base" aria-hidden="true" />
            Supprimer
          </button>
        </div>
      </section>

      <section className="flex flex-col rounded-lg border border-background-200 bg-background-50">
        <header className="flex items-center gap-2.5 border-b border-background-200 px-4 py-3.5 md:px-5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary-50 text-secondary-600">
            <i className="ri-information-line text-base" aria-hidden="true" />
          </span>
          <h3 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
            Informations du locataire
          </h3>
        </header>
        <div className="p-4 md:p-5">
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {infos.map((info) => (
              <div key={info.label} className="flex items-start gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-background-100 text-foreground-500">
                  <i className={cn(info.icon, "text-base")} aria-hidden="true" />
                </span>
                <div className="min-w-0">
                  <dt className="text-xs text-foreground-500">{info.label}</dt>
                  <dd className="truncate text-sm font-label font-medium text-foreground-950">{info.value}</dd>
                </div>
              </div>
            ))}
          </dl>
          {locataire.notes && (
            <div className="mt-5 border-t border-background-200 pt-4">
              <p className="text-xs font-label font-semibold uppercase tracking-wide text-foreground-400">Notes</p>
              <p className="mt-1.5 text-sm leading-relaxed text-foreground-700">{locataire.notes}</p>
            </div>
          )}
        </div>
      </section>

      <PlatformLocataireForm open={editOpen} onClose={() => setEditOpen(false)} locataire={locataire} />

      <ConfirmDeleteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={confirmDelete}
        description={`Supprimer le locataire « ${nom} » ? Cette action est définitive.`}
        blockedMessage={
          nbContrats > 0
            ? `Ce locataire est relié à ${nbContrats} contrat(s). Clôturez ou supprimez d'abord ses contrats.`
            : undefined
        }
      />
    </div>
  );
}